import React, { useCallback, useEffect, useState } from 'react'

import { useInView } from 'react-intersection-observer'
import noop from 'lib/function/noop'
import TextButton from 'components/Luxkit/Button/TextButton'
import Typography from 'components/Luxkit/Typography/Typography'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'

const SCROLL_NEAR_THRESHOLD = 500

interface Props extends Omit<React.ComponentProps<typeof TextButton>, 'kind'> {
  currentPage: number;
  defaultPage: number;
  onScrollNear?: () => void;
  showLoadingIndicator?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onVisibilityChange?: (isVisible: boolean) => void;
}

function OfferListLoadMoreButton(props: Props) {
  const {
    children,
    onScrollNear = noop,
    showLoadingIndicator = false,
    onClick,
    onVisibilityChange,
    currentPage,
    defaultPage,
    ...buttonProps
  } = props

  const [shouldSkip, setShouldSkip] = useState<boolean>(true)

  const [buttonRef, isInView] = useInView({
    rootMargin: `${SCROLL_NEAR_THRESHOLD}px`,
    skip: shouldSkip,
  })

  const handleScroll = useCallback(() => {
    setShouldSkip(false)
  }, [])

  useEffect(() => {
    if (currentPage === defaultPage) {
      setShouldSkip(true)
      window.addEventListener('scroll', handleScroll)
    }
    return () => window.removeEventListener('scroll', handleScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  useEffect(() => {
    if (!shouldSkip) {
      window.removeEventListener('scroll', handleScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSkip])

  useEffect(() => {
    if (isInView) {
      onScrollNear()
    }
    onVisibilityChange?.(isInView)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView])

  const handleButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur()
    handleScroll()
    if (onClick) onClick(event)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClick])

  return (
    <VerticalSpacer gap={16} ref={buttonRef}>
      {showLoadingIndicator && (
        <LoadingIndicator inline />
      )}
      {!showLoadingIndicator && <Typography align="center">
        <TextButton kind="primary" onClick={handleButtonClick} {...buttonProps}>
          {children}
        </TextButton>
      </Typography>}
    </VerticalSpacer>
  )
}

export default OfferListLoadMoreButton
